import type { ComponentType, LazyExoticComponent, ReactElement, ReactNode } from 'react';
import React, { Suspense, useEffect } from 'react';
import { observer } from 'mobx-react';
import useStores from 'hooks/use-stores.hooks';
import ErrorBoundary from 'components/common/error-boundary';
import Loader from 'components/common/loader';
import PermissionContainer from 'components/common/permission-container';
import ForbiddenRoute from 'components/routes/forbidden-route';
import { useCurrentProjectId } from 'hooks/path-params-extraction.hooks';
import { KbarWrapper } from 'components/kbar/kbar-wrapper';
import { useCuratedProjects } from 'stores/rq/projects';
import type { RolesApi } from '@env0/role-service/api';
import type { PermissionOperator } from 'hooks/use-has-permission';
import { useIdleSessionLogout } from 'hooks/use-idle-session-logout';
import { useAddOrganizationIdSearchParam } from 'hooks/use-add-organization-id-search-param';

type AppRouteProps = {
  LazyComponent: LazyExoticComponent<ComponentType<any>> | ComponentType<any>;
  rbacPermissions?: RolesApi.RBACPermission[];
  permissionOperator?: PermissionOperator;
  isPublic?: boolean;
  children?: (content: ReactNode) => ReactElement;
};

const AppRoute: React.FunctionComponent<AppRouteProps> = ({
  LazyComponent,
  rbacPermissions,
  permissionOperator,
  isPublic,
  children = content => <>{content}</>
}) => {
  const { authStore } = useStores();
  const { isFetched: fetchedProjects } = useCuratedProjects({ enabled: authStore.isAuthenticated });
  const isProjectRoute = !!useCurrentProjectId();

  useAddOrganizationIdSearchParam();

  // Login if necessary, happens when you reach a public route for the first time
  useEffect(() => {
    if (!isPublic) {
      authStore.login();
    }
  }, [isPublic, authStore]);

  // Check session expiry when going to a different page (LazyComponent changes)
  useEffect(() => {
    if (!isPublic) {
      authStore.checkSession();
    }
  }, [LazyComponent, authStore, isPublic]);

  useIdleSessionLogout();

  const loader = <Loader size={'large'} type={'robot'} />;

  const loaderPage = children(<Loader size={'large'} type={'robot'} />);

  if (!isPublic && !authStore.isAuthenticated) {
    return null;
  }

  if (isProjectRoute) {
    if (!isPublic && authStore.isAuthenticated && !fetchedProjects) {
      return loaderPage;
    }
  }

  return children(
    <Suspense fallback={loader}>
      <ErrorBoundary renderErrorMessageOnError>
        <PermissionContainer
          fallback={ForbiddenRoute}
          requiredPermissions={rbacPermissions}
          permissionOperator={permissionOperator}>
          <LazyComponent />
          <KbarWrapper data-e2e={'kbar'} />
        </PermissionContainer>
      </ErrorBoundary>
    </Suspense>
  );
};

export default observer(AppRoute);
